import React, { useState } from 'react';
import classNames from 'classnames';
import { Product } from '@/types/product';
import { useAtom } from 'jotai';
import { activeIndexAtom, loadingAtom, productsAtom } from '@/store';
import { getDisplayPrice } from '@/utils/vip';
import { handleRefreshQrCode } from '@/utils/subscribe';
import MeidouBean from '@/assets/image/meidou/meidou-price-bg.png';
import PayQrModal from '../pay-qr-modal';
import styles from './index.module.css';
import { track } from '@/utils/common';
import IconFont from '@/components/icon-font';
import { useLocaleStore } from '@/i18n/localeStore';

const SKELETON_PLACEHOLDER = Array.from({ length: 6 }).map((_, i) => ({})) as unknown as Product[];

const MeidouProductGrid: React.FC = () => {
  const { $t } = useLocaleStore();
  const [products] = useAtom(productsAtom);
  const [activeIndex, setActiveIndex] = useAtom(activeIndexAtom);
  const [loading] = useAtom(loadingAtom);
  const [visiblePayQr, setVisiblePayQr] = useState(false);

  const isSkeleton = !products.length;
  const displayProducts = isSkeleton ? SKELETON_PLACEHOLDER : products;

  const handleSelect = (index: number, action?: 'openPayQr') => {
    if (loading) return;
    const data = products[index];
    setActiveIndex(index);
    handleRefreshQrCode();
    if (action === 'openPayQr') {
      setVisiblePayQr(true);

      track({
        event_id: 'vip_page_click',
        event_name: '会员落地页点击',
        params: { class: 'pay_now', name_id: data?.productId || '' },
      });
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params: { class: 'name_id', name_id: data?.productId || '' },
    });
  };

  const renderCard = (data: Product, index: number) => {
    const count = data.meidouRights?.count || 0;
    const countText = count >= 100000 ? `${count / 10000}万` : count || '--';
    const displayPrice = getDisplayPrice(data);
    const isActive = !isSkeleton && activeIndex === index;

    // 单价计算
    const price = displayPrice.price || 0;

    return (
      <div
        key={data.productId || index}
        className={classNames(styles.card, {
          [styles.cardActive]: isActive,
          [styles.skeleton]: isSkeleton,
        })}
        onClick={() => handleSelect(index)}
      >
        {/* 左上角标签 */}
        {data.commonDesc && <div className={styles.badge}>{data.commonDesc}</div>}

        <div className={styles.cardContent}>
          <div className={styles.meidouInfo}>
            <img className={styles.meidouIcon} src={MeidouBean} alt="" />
            <span className={styles.meidouCount}>{countText}</span>
          </div>
          <div className={styles.unitPrice}>{data.productDesc}</div>
        </div>

        <div
          className={classNames(styles.priceBtn, { [styles.priceBtnActive]: isActive })}
          onClick={(e) => {
            e.stopPropagation();
            handleSelect(index, 'openPayQr');
          }}
        >
          {displayPrice.moneySymbol || '¥'} {price || '--'}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.grid}>
        {displayProducts.map((product, index) => renderCard(product as Product, index))}
      </div>
      {!isSkeleton ? (
        <div className={styles.tips}>
          <IconFont name="infoCircleBold" fontSize={18} color="rgba(255, 255, 255, 0.50)" />
          {$t((v) => v.Text_ValidOneYear)}
        </div>
      ) : null}

      <PayQrModal visible={visiblePayQr} onClose={() => setVisiblePayQr(false)} />
    </>
  );
};

export default MeidouProductGrid;
